import type { AjaxService } from 'AjaxService';
import type { IBemerkungenViewModel } from './bemerkungen.component';
import { bereinigterInhalt, textAlsHtml } from './bemerkungen-html.model';

export function druckDokument(doc: Document, bemerkung: IBemerkungenViewModel, urls: Map<string, string>) {
  doc.title = bemerkung.betreff || 'Bemerkung';
  doc.documentElement.lang = 'de';
  const style = doc.createElement('style');
  // Ohne Seitenrand erzeugt der Browser keine URL-Fußzeile. Der Innenabstand
  // wird bei Seitenumbrüchen geklont und hält den Inhalt vom Papierrand fern.
  style.textContent = `
    @page { margin: 0; }
    body { margin: 0; font: 11pt Arial, sans-serif; color: #111; overflow-wrap: anywhere; }
    main { padding: 18mm; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
    h1 { margin-top: 0; font-size: 16pt; }
    img { max-width: 100%; height: auto; max-height: 240mm; object-fit: contain; break-inside: avoid; }
    p { margin: 0 0 0.7em; }
    .meta { margin-bottom: 1.5em; white-space: pre-line; }
  `;
  doc.head.appendChild(style);
  const main = doc.createElement('main');
  const title = doc.createElement('h1');
  title.textContent = bemerkung.betreff;
  const meta = doc.createElement('p');
  meta.className = 'meta';
  meta.textContent = [
    'Erstellt: ' + new Date(bemerkung.erstellt).toLocaleDateString('de-DE'),
    bemerkung.benutzerName ? 'Verfasser/-in: ' + bemerkung.benutzerName : '',
    'Gehört zu: ' + (bemerkung.gehoertZu || '')
  ].filter(Boolean).join('\n');
  const inhalt = bereinigterInhalt(bemerkung.textHtml ?? textAlsHtml(bemerkung.text), urls);
  main.append(title, meta, doc.importNode(inhalt, true));
  doc.body.replaceChildren(main);
}

export async function bemerkungDrucken(bemerkung: IBemerkungenViewModel, ajax: AjaxService): Promise<void> {
  // Direkt beim Klick öffnen, bevor asynchrone Bildabrufe die Benutzeraktion verlassen.
  const fenster = window.open('', '_blank');
  if (!fenster) throw new Error('Bitte erlauben Sie das Öffnen des Druckfensters und klicken Sie erneut auf Drucken.');
  fenster.opener = null;
  fenster.document.title = 'Druckansicht wird vorbereitet';
  fenster.document.body.textContent = 'Druckansicht wird vorbereitet …';
  const urls = new Map<string, string>();
  try {
    for (const bild of bemerkung.bilder || []) {
      if (fenster.closed) return;
      const blob = await ajax.downloadResource(`api/Bemerkungen/${bemerkung.id}/Bilder/${bild.bildId}`);
      urls.set(bild.bildId, URL.createObjectURL(blob));
    }
    if (fenster.closed) return;
    druckDokument(fenster.document, bemerkung, urls);
    await Promise.all(Array.from(fenster.document.images).map(img => img.decode()));
    if (fenster.closed) return;
    fenster.focus();
    fenster.print();
  } catch {
    throw new Error('Die Druckansicht konnte nicht vollständig geladen werden. Bitte klicken Sie erneut auf Drucken.');
  } finally {
    if (!fenster.closed) fenster.close();
    urls.forEach(url => URL.revokeObjectURL(url));
  }
}
